/**
 * The module catalogue, as the constructor reads it (MODULES §7, §8.3).
 *
 * Everything here is written in terms of *kinds* of contribution — actions,
 * values, commands — and never in terms of a particular module (M6). Nothing
 * in `web/` names `location`: a release that carries a new module lights up
 * the same pickers, the same preview and the same device panel with no change
 * here.
 *
 * `GET /modules` hands back the manifests unchanged, so this file is the one
 * place that knows how to read them.
 */

import type { ModuleOut } from "../api/types";
import { localized } from "../i18n/localized";
import {
  MODULE_ACTION,
  MODULE_BLOCK,
  isModuleRow,
  type ActionSpec,
  type BlockSpec,
  type ContributionIndex,
  type ModuleBlockRef,
  type RowRef,
  type Spec,
} from "./spec";

/**
 * The little a catalogue entry needs for the index — which is exactly what a
 * manifest already has, so the fixture parity tests build one from the
 * `docs/modules/fixtures` manifests without inventing a `ModuleOut`.
 */
export interface CatalogueEntry {
  id: string;
  enabled?: boolean;
  actions?: Record<string, ActionSpec | undefined>;
  blocks?: Record<string, BlockSpec | undefined>;
  values?: Record<string, { status_row?: boolean } | undefined>;
}

/** The catalogue as `validateSpec` asks it (MODULES §7.3). */
export function buildIndex(modules: CatalogueEntry[]): ContributionIndex {
  const known = new Set<string>();
  const enabled = new Set<string>();
  const actions = new Map<string, ReadonlyMap<string, ActionSpec>>();
  const blocks = new Map<string, ReadonlyMap<string, BlockSpec>>();
  const rows = new Map<string, ReadonlySet<string>>();
  for (const module of modules) {
    known.add(module.id);
    if (module.enabled) enabled.add(module.id);
    // The action *body* rides along, not just its id: a button's `params` are
    // checked against the `params` the manifest declares here (MODULES §2.6).
    actions.set(
      module.id,
      new Map(Object.entries(module.actions ?? {}).map(([id, entry]) => [id, entry ?? {}])),
    );
    // And a block's, for the same reason one level up (MODULES §7.4).
    blocks.set(
      module.id,
      new Map(Object.entries(module.blocks ?? {}).map(([id, entry]) => [id, entry ?? {}])),
    );
    rows.set(
      module.id,
      // A value that is not a status row reads as absent, which is exactly
      // what `design_unknown_contribution` means (MODULES §7.3).
      new Set(
        Object.entries(module.values ?? {})
          .filter(([, value]) => value?.status_row === true)
          .map(([id]) => id),
      ),
    );
  }
  return { known, enabled, actions, blocks, rows };
}

/** The module's own name, or its id when the catalogue has never heard of it. */
export function moduleName(modules: ModuleOut[], moduleId: string, lang: string): string {
  const module = modules.find((entry) => entry.id === moduleId);
  return localized(module?.name, lang, moduleId);
}

/**
 * `android.permission.ACCESS_FINE_LOCATION` → `Access fine location`.
 *
 * The 🔒 hint on an action names what the phone will ask for, and the whole
 * constant is too much shouting for a sentence a customer reads.
 */
export function permissionShortName(permission: string): string {
  const last = permission.split(".").pop() ?? permission;
  const words = last.toLowerCase().replace(/_/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : permission;
}

/** One module's contribution to one design, for the "uses" line (§8.7). */
export interface ModuleUsage {
  module: string;
  /** Distinct action ids the design's buttons name. */
  actions: string[];
  /** Distinct value ids its status rows name. */
  rows: string[];
  /** Distinct block ids the design places (MODULES §7.4). */
  blocks: string[];
}

/**
 * What each module contributes to *this* document — what the APK page's
 * *Design "X" uses: Location (2 actions, 2 values, 0 blocks)* line counts.
 *
 * Written to survive a malformed document, like `referencedAssets`: it also
 * runs over a stored spec, and one that no longer validates still has to be
 * readable.
 */
export function moduleUsage(spec: Spec | undefined): ModuleUsage[] {
  const found = new Map<string, { actions: Set<string>; rows: Set<string>; blocks: Set<string> }>();
  const entry = (moduleId: string) => {
    const existing = found.get(moduleId);
    if (existing) return existing;
    const fresh = {
      actions: new Set<string>(),
      rows: new Set<string>(),
      blocks: new Set<string>(),
    };
    found.set(moduleId, fresh);
    return fresh;
  };

  for (const block of spec?.blocks ?? []) {
    if (block?.type === "button" && block.action?.type === MODULE_ACTION) {
      const { module, action } = block.action;
      if (typeof module === "string") entry(module).actions.add(String(action));
    }
    if (block?.type === MODULE_BLOCK) {
      const { module, block: name } = block;
      if (typeof module === "string") entry(module).blocks.add(String(name));
    }
    if (block?.type === "status") {
      for (const row of block.rows ?? []) {
        if (typeof row !== "string" || !isModuleRow(row)) continue;
        const [moduleId, value] = splitRow(row);
        entry(moduleId).rows.add(value);
      }
    }
  }

  return [...found.entries()]
    .map(([module, use]) => ({
      module,
      actions: [...use.actions].sort(),
      rows: [...use.rows].sort(),
      blocks: [...use.blocks].sort(),
    }))
    .sort((a, b) => a.module.localeCompare(b.module));
}

/** Module ids a design names, in an action or a status row. */
export function referencedModules(spec: Spec | undefined): string[] {
  return moduleUsage(spec).map((use) => use.module);
}

/** `location.last_fix` → `["location", "last_fix"]`. */
export function splitRow(row: string): [string, string] {
  const dot = row.indexOf(".");
  return [row.slice(0, dot), row.slice(dot + 1)];
}

/**
 * What the phone preview shows where the phone would show the real thing:
 * every enabled module's status-row values, keyed `<module>.<value>` with the
 * manifest's own label and `samples.default` (MODULES §2.7).
 */
export function sampleRows(
  modules: ModuleOut[],
  lang: string,
): Record<string, { label: string; value: string }> {
  const samples: Record<string, { label: string; value: string }> = {};
  for (const module of modules) {
    for (const [id, value] of Object.entries(module.values ?? {})) {
      samples[`${module.id}.${id}`] = {
        label: localized(value.label, lang, id),
        value: value.samples?.default ?? "—",
      };
    }
  }
  return samples;
}

/**
 * What the preview needs to draw a module block where the phone draws the
 * module's own view: the manifest's label and how tall it is, keyed
 * `<module>.<block>` (MODULES §7.4).
 *
 * Every module, not only the enabled ones — a design that names a switched-off
 * module still has to draw, faded, rather than vanish.
 */
export function sampleBlocks(
  modules: ModuleOut[],
  lang: string,
): Record<string, { label: string; height: "content" | "flex" }> {
  const samples: Record<string, { label: string; height: "content" | "flex" }> = {};
  for (const module of modules) {
    for (const [id, block] of Object.entries(module.blocks ?? {})) {
      samples[`${module.id}.${id}`] = {
        label: localized(block.label, lang, id),
        height: block.height === "flex" ? "flex" : "content",
      };
    }
  }
  return samples;
}

/** The manifest entry a `module` block names, or undefined if nothing does. */
export function moduleBlockOf(modules: ModuleOut[], block: ModuleBlockRef) {
  return modules.find((entry) => entry.id === block.module)?.blocks?.[block.block];
}

/** What to call a module block: the manifest's label, else `<module>.<block>`. */
export function moduleBlockName(
  modules: ModuleOut[],
  block: ModuleBlockRef,
  lang: string,
): string {
  return localized(moduleBlockOf(modules, block)?.label, lang, `${block.module}.${block.block}`);
}

/** Blocks the palette may offer: one per block of every *enabled* module. */
export function moduleBlockChoices(
  modules: ModuleOut[],
  lang: string,
): Array<{ module: string; block: string; label: string; description: string }> {
  return modules
    .filter((module) => module.enabled)
    .flatMap((module) =>
      Object.entries(module.blocks ?? {}).map(([id, block]) => ({
        module: module.id,
        block: id,
        label: localized(block.label, lang, id),
        description: localized(block.description, lang),
      })),
    );
}

/** Rows a status block may offer: `<module>.<value>` for every enabled module. */
export function moduleRowChoices(
  modules: ModuleOut[],
  lang: string,
): Array<{ module: string; moduleName: string; row: RowRef; label: string }> {
  return modules
    .filter((module) => module.enabled)
    .flatMap((module) =>
      Object.entries(module.values ?? {})
        .filter(([, value]) => value?.status_row === true)
        .map(([id, value]) => ({
          module: module.id,
          moduleName: localized(module.name, lang, module.id),
          row: `${module.id}.${id}` as RowRef,
          label: localized(value.label, lang, id),
        })),
    );
}
